/**
 * Model Catalog (Track B-4)
 *
 * Read-only view of the unified model catalog served by
 * GET /v1/admin/models/catalog — every modality the deployment serves, with
 * dual descriptors (billing + capability) and live dora editorial overlay.
 */

import { useMemo, useState } from 'react';

import {
  CapabilityWarningBadge,
  SourceHealthBanner,
  type SourceStatus,
} from '@/components/model-display';
import type { CatalogModel, Modality, ModelCatalogResponse } from '@/types';
import { useModelCatalog } from '@/lib/queries/useModelsQuery';
import { formatBilling, modalityLabel } from './catalogFormat';

const MODALITIES: Modality[] = ['text', 'image', 'tts', 'stt', 'embedding', 'rerank'];

interface ModelCatalogPanelProps {
  initialModality?: Modality | 'all';
}

type CatalogWithTrustSignals = ModelCatalogResponse & {
  sources?: SourceStatus[];
};

function capabilityInconsistent(model: CatalogModel): boolean {
  return 'capability_inconsistent' in model && model.capability_inconsistent === true;
}

export function ModelCatalogPanel({ initialModality = 'all' }: ModelCatalogPanelProps) {
  const { data, isLoading, error } = useModelCatalog();
  const [modality, setModality] = useState<Modality | 'all'>(initialModality);
  const sources = (data as CatalogWithTrustSignals | undefined)?.sources;

  const models = useMemo(() => {
    const all = data?.models ?? [];
    return modality === 'all' ? all : all.filter((m) => m.capability.modality === modality);
  }, [data, modality]);

  if (isLoading) {
    return (
      <div data-testid="catalog-loading" className="p-6 text-sm text-gray-500">
        Loading model catalog…
      </div>
    );
  }
  if (error) {
    return (
      <div data-testid="catalog-error" className="p-6 text-sm text-red-600">
        Failed to load model catalog: {error.message}
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      <SourceHealthBanner sources={sources} className="rounded-lg border" />

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Model Catalog</h2>
        <span className="text-sm text-gray-500">
          {/* count = currently visible rows (respects the modality filter);
              total = full catalog size, so the two never contradict. */}
          <span data-testid="catalog-count">{models.length}</span> shown ·{' '}
          <span data-testid="catalog-total">{data?.count ?? data?.models?.length ?? models.length}</span> total
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        {(['all', ...MODALITIES] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setModality(m)}
            className={`rounded px-2 py-1 text-xs ${
              modality === m ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
            }`}
          >
            {m === 'all' ? 'All' : modalityLabel(m)}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b text-left text-gray-500">
              <th className="py-2 pr-4">ID</th>
              <th className="py-2 pr-4">Modality</th>
              <th className="py-2 pr-4">Billing</th>
              <th className="py-2 pr-4">Tier</th>
              <th className="py-2 pr-4">Quality</th>
              <th className="py-2 pr-4">Org</th>
              <th className="py-2 pr-4">Tags</th>
            </tr>
          </thead>
          <tbody>
            {models.map((model: CatalogModel) => (
              <tr key={model.id} className="border-b">
                <td className="py-2 pr-4 font-mono">
                  <span className="inline-flex items-center gap-1.5">
                    <span>{model.id}</span>
                    <CapabilityWarningBadge
                      inconsistent={capabilityInconsistent(model)}
                      className="font-sans"
                    />
                  </span>
                </td>
                <td className="py-2 pr-4">{modalityLabel(model.capability.modality)}</td>
                <td className="py-2 pr-4">{formatBilling(model.billing)}</td>
                <td className="py-2 pr-4">{model.routing_tier ?? '-'}</td>
                <td className="py-2 pr-4">{model.quality_tier || '-'}</td>
                <td className="py-2 pr-4">{model.organization || '-'}</td>
                <td className="py-2 pr-4">{(model.tags ?? []).join(', ') || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ModelCatalogPanel;
